import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PanelLeft, Search, Sparkles } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import {
  AUX_DESTINATIONS,
  NAV_GROUPS,
  expandNavGroupsWithHubTabs,
  filterNavGroupsForPermissions,
  isSettingsMenuDestination,
  railPathFor,
  userCanAccessDestination,
} from '@/navigation/registry';
import { isCollapsedNavDestination, isHubTabDestinationId, railLabelFor } from '@/navigation/hubs';
import { useAuth } from '@/features/auth/authContext';
import { useGlobalSearch, useSearchCommandCenter } from '@/features/search/search.hooks';
import { useUiStore } from '@/stores/uiStore';

/**
 * Global command palette (⌘K / Ctrl+K): module navigation, shell actions,
 * tenant-scoped record search (S30), and seeded quick actions.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const open = useUiStore((state) => state.commandPaletteOpen);
  const setOpen = useUiStore((state) => state.setCommandPaletteOpen);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const toggleAdvisor = useUiStore((state) => state.toggleAdvisor);
  const permissions = useAuth().permissions;
  const canSearch = hasPermission(permissions, PERMISSIONS.SEARCH_READ);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchQuery = useGlobalSearch(debouncedQuery, open && canSearch);
  const searchCenterQuery = useSearchCommandCenter(open && canSearch);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      if (!cancelled) {
        setDebouncedQuery(query.trim());
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const navGroups = expandNavGroupsWithHubTabs(
    filterNavGroupsForPermissions(NAV_GROUPS, permissions),
    permissions,
  );
  const systemDestinations = AUX_DESTINATIONS.filter(
    (item) =>
      item.id !== 'auth' &&
      !isSettingsMenuDestination(item.id) &&
      !isHubTabDestinationId(item.id) &&
      !isCollapsedNavDestination(item.id) &&
      userCanAccessDestination(item, permissions),
  );
  const hits = searchQuery.data?.hits ?? [];
  const searchActions = searchQuery.data?.quickActions ?? [];
  const centerActions = searchCenterQuery.data?.quickActions ?? [];
  // Prefer search payload when it has actions; otherwise use command-center bootstrap.
  const quickActions = searchActions.length > 0 ? searchActions : centerActions;

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={debouncedQuery.length < 2}>
      <CommandInput
        placeholder="Search modules, records, actions…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searchQuery.isFetching ? 'Searching records…' : 'No results found.'}
        </CommandEmpty>

        {debouncedQuery.length >= 2 && hits.length > 0 ? (
          <CommandGroup heading="Records">
            {hits.map((hit) => (
              <CommandItem
                key={`${hit.kind}-${hit.id}`}
                value={`${hit.title} ${hit.subtitle} ${hit.kind}`}
                onSelect={() => run(() => navigate(hit.href))}
              >
                <Search className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">
                  {hit.title}
                  <span className="text-muted-foreground ml-2 text-xs">{hit.subtitle}</span>
                </span>
                <span className="text-muted-foreground text-2xs ml-auto">{hit.kind}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {navGroups.map((group) => (
          <CommandGroup key={group.id} heading={group.label}>
            {group.items.map((item) => {
              const Icon = item.icon;
              const label = railLabelFor(item.id, item.label);
              return (
                <CommandItem
                  key={item.id}
                  value={`${label} ${item.label} ${item.description}`}
                  onSelect={() => run(() => navigate(railPathFor(item, permissions)))}
                >
                  <Icon className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 truncate">{label}</span>
                  <span className="text-muted-foreground text-2xs ml-auto">
                    {item.sprint.split('_')[0]}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}

        {systemDestinations.length > 0 ? (
          <CommandGroup heading="System">
            {systemDestinations.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.id}
                  value={`${item.label} ${item.description}`}
                  onSelect={() => run(() => navigate(item.path))}
                >
                  <Icon className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="text-muted-foreground text-2xs ml-auto">
                    {item.sprint.split('_')[0]}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ) : null}

        {quickActions.length > 0 ? (
          <CommandGroup heading="Quick actions">
            {quickActions.map((action) => (
              <CommandItem
                key={action.id}
                value={`${action.label} ${action.description}`}
                onSelect={() => run(() => navigate(action.href))}
              >
                <Sparkles className="text-ai h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate">{action.label}</span>
                {action.shortcut ? (
                  <span className="text-muted-foreground text-2xs ml-auto">{action.shortcut}</span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem value="toggle sidebar collapse" onSelect={() => run(toggleSidebar)}>
            <PanelLeft className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Toggle sidebar</span>
          </CommandItem>
          <CommandItem value="toggle ai advisor drawer" onSelect={() => run(toggleAdvisor)}>
            <Sparkles className="text-ai h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Toggle AI advisor</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
